// SharedInstructionsPanel — Phase 2b of the revised Pamosi Clinic EMR.
//
// Patient-level sticky notes between staff. Mounted on patient registration
// (reception) + encounter detail (clinical) so both ends of the hand-off
// can see "latex allergy", "prefers morning slots", "difficult IV left arm".
//
// Contract:
//   - props.patientId is the only wiring the parent provides; everything
//     else is self-contained (data fetch, mutations, local error state).
//   - Keeps resolved notes collapsed under a "Show resolved" toggle so the
//     active panel stays short even on patients with lots of history.
//   - Pinned notes always sort to the top; the list is otherwise newest-first.
//
// Role gating:
//   The server allows every clinic role to view + write; the only gated
//   action is body-edit + delete (author or admin), which this panel
//   handles by only showing those controls to the author (clinicianId
//   match) and hiding them otherwise. Admin override is still enforced
//   server-side.

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PatientSharedInstructionView } from '@pamosi/shared';
import { ApiError, inboxApi, instructionsApi } from '../lib/api';
import { useAuth } from '../lib/auth';
import { MentionTextarea } from './MentionTextarea';

const MAX_BODY = 2000;

export function SharedInstructionsPanel({ patientId }: { patientId: string }) {
    const [showResolved, setShowResolved] = useState(false);
    const [draft, setDraft] = useState('');
    const [draftPinned, setDraftPinned] = useState(false);
    const [createError, setCreateError] = useState<string | null>(null);

    const qc = useQueryClient();
    const queryKey = ['patient-instructions', patientId, showResolved] as const;
    const listQ = useQuery({
        queryKey,
        queryFn: () => instructionsApi.list(patientId, { includeResolved: showResolved }),
    });
    // Mention candidates are clinic-scoped (not patient-scoped) so we share
    // this cache across every mounted SharedInstructionsPanel. Served from
    // the inbox router's /candidates endpoint — any clinic member can read.
    const candidatesQ = useQuery({
        queryKey: ['mention-candidates'],
        queryFn: () => inboxApi.candidates(),
        staleTime: 60_000,
    });
    const candidates = candidatesQ.data?.candidates ?? [];

    const invalidate = () => {
        void qc.invalidateQueries({ queryKey: ['patient-instructions', patientId] });
    };

    const create = useMutation({
        mutationFn: (args: { body: string; pinned: boolean }) =>
            instructionsApi.create(patientId, args),
        onSuccess: () => {
            setDraft('');
            setDraftPinned(false);
            setCreateError(null);
            invalidate();
        },
        onError: (e) => setCreateError(prettyError(e)),
    });

    const onAdd = () => {
        const trimmed = draft.trim();
        if (!trimmed) return;
        if (trimmed.length > MAX_BODY) {
            setCreateError('Too long. Keep it under 2000 characters.');
            return;
        }
        create.mutate({ body: trimmed, pinned: draftPinned });
    };

    const { active, resolved } = useMemo(() => {
        const items = listQ.data?.instructions ?? [];
        return {
            active: items.filter((i) => !i.resolved_at),
            resolved: items.filter((i) => !!i.resolved_at),
        };
    }, [listQ.data]);

    return (
        <section className="pm-card p-4 space-y-3">
            <div className="flex items-start justify-between gap-2">
                <div>
                    <h3 className="text-slate-900 font-medium">Shared instructions</h3>
                    <p className="text-slate-500 text-xs mt-0.5">
                        Notes between staff about this patient. Visible on reception + consult.
                    </p>
                </div>
                {resolved.length > 0 && (
                    <button
                        type="button"
                        className="pm-btn-ghost text-xs"
                        onClick={() => setShowResolved((v) => !v)}
                    >
                        {showResolved ? 'Hide resolved' : `Show resolved (${resolved.length})`}
                    </button>
                )}
            </div>

            <div className="space-y-2">
                <MentionTextarea
                    value={draft}
                    onChange={setDraft}
                    candidates={candidates}
                    maxLength={MAX_BODY}
                    placeholder="Add a note for the next person who sees this patient. Type @ to tag a teammate."
                />
                <div className="flex items-center justify-between gap-2">
                    <label className="text-slate-600 text-xs flex items-center gap-1.5">
                        <input
                            type="checkbox"
                            checked={draftPinned}
                            onChange={(e) => setDraftPinned(e.target.checked)}
                        />
                        Pin to top
                    </label>
                    <button
                        type="button"
                        className="pm-btn-primary text-xs"
                        disabled={create.isPending || !draft.trim()}
                        onClick={onAdd}
                    >
                        {create.isPending ? 'Adding…' : 'Add note'}
                    </button>
                </div>
                {createError && <div className="text-rose-700 text-xs">{createError}</div>}
            </div>

            {listQ.isLoading && (
                <div className="text-slate-500 text-xs">Loading…</div>
            )}
            {listQ.isError && (
                <div className="text-rose-700 text-xs">Could not load instructions.</div>
            )}
            {listQ.data && active.length === 0 && (
                <div className="text-slate-500 text-xs">No active notes.</div>
            )}

            {active.length > 0 && (
                <ul className="space-y-2">
                    {active.map((i) => (
                        <InstructionRow key={i.id} patientId={patientId} item={i} candidates={candidates} />
                    ))}
                </ul>
            )}

            {showResolved && resolved.length > 0 && (
                <>
                    <div className="text-slate-400 text-xs uppercase tracking-wide pt-2">Resolved</div>
                    <ul className="space-y-2 opacity-70">
                        {resolved.map((i) => (
                            <InstructionRow key={i.id} patientId={patientId} item={i} candidates={candidates} />
                        ))}
                    </ul>
                </>
            )}
        </section>
    );
}

function InstructionRow(
    { patientId, item, candidates }: {
        patientId: string;
        item: PatientSharedInstructionView;
        candidates: import('@pamosi/shared').MentionCandidate[];
    },
) {
    const { clinician } = useAuth();
    const qc = useQueryClient();
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(item.body);
    const [error, setError] = useState<string | null>(null);

    const canEdit = !!clinician && clinician.id === item.author_clinician_id;

    const invalidate = () => {
        void qc.invalidateQueries({ queryKey: ['patient-instructions', patientId] });
    };

    const update = useMutation({
        mutationFn: (args: { body?: string; pinned?: boolean; resolved?: boolean }) =>
            instructionsApi.update(patientId, item.id, args),
        onSuccess: () => {
            setEditing(false);
            setError(null);
            invalidate();
        },
        onError: (e) => setError(prettyError(e)),
    });

    const remove = useMutation({
        mutationFn: () => instructionsApi.remove(patientId, item.id),
        onSuccess: () => invalidate(),
        onError: (e) => setError(prettyError(e)),
    });

    const busy = update.isPending || remove.isPending;
    const resolved = !!item.resolved_at;

    const timestamp = formatTimestamp(item.created_at);
    const authorLabel = item.author_full_name?.trim() || 'Unknown';
    const resolvedBy = item.resolved_by_full_name?.trim() || null;

    return (
        <li className="rounded border border-slate-200 bg-white p-3">
            <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                    {item.pinned && !resolved && (
                        <span className="text-xs uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">
                            Pinned
                        </span>
                    )}
                    <span className="text-slate-500 text-xs">{authorLabel} · {timestamp}</span>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                    {!resolved && (
                        <button
                            type="button"
                            className="pm-btn-ghost text-xs"
                            disabled={busy}
                            onClick={() => update.mutate({ pinned: !item.pinned })}
                        >
                            {item.pinned ? 'Unpin' : 'Pin'}
                        </button>
                    )}
                    <button
                        type="button"
                        className="pm-btn-ghost text-xs"
                        disabled={busy}
                        onClick={() => update.mutate({ resolved: !resolved })}
                    >
                        {resolved ? 'Reopen' : 'Resolve'}
                    </button>
                    {canEdit && !resolved && !editing && (
                        <button
                            type="button"
                            className="pm-btn-ghost text-xs"
                            disabled={busy}
                            onClick={() => { setDraft(item.body); setEditing(true); }}
                        >
                            Edit
                        </button>
                    )}
                    {canEdit && (
                        <button
                            type="button"
                            className="pm-btn-ghost text-xs text-rose-700"
                            disabled={busy}
                            onClick={() => {
                                if (window.confirm('Delete this note?')) remove.mutate();
                            }}
                        >
                            Delete
                        </button>
                    )}
                </div>
            </div>

            {editing ? (
                <div className="mt-2 space-y-2">
                    <MentionTextarea
                        value={draft}
                        onChange={setDraft}
                        candidates={candidates}
                        maxLength={MAX_BODY}
                    />
                    <div className="flex items-center gap-2 justify-end">
                        <button
                            type="button"
                            className="pm-btn-outline text-xs"
                            disabled={busy}
                            onClick={() => { setEditing(false); setError(null); }}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="pm-btn-primary text-xs"
                            disabled={busy || !draft.trim() || draft.trim() === item.body}
                            onClick={() => update.mutate({ body: draft.trim() })}
                        >
                            {update.isPending ? 'Saving…' : 'Save'}
                        </button>
                    </div>
                </div>
            ) : (
                <div className={
                    'mt-1 text-sm whitespace-pre-wrap '
                    + (resolved ? 'text-slate-500 line-through' : 'text-slate-800')
                }>
                    {item.body}
                </div>
            )}
            {resolved && resolvedBy && (
                <div className="text-slate-400 text-xs mt-1">
                    Resolved by {resolvedBy} · {formatTimestamp(item.resolved_at!)}
                </div>
            )}
            {error && <div className="text-rose-700 text-xs mt-2">{error}</div>}
        </li>
    );
}

function formatTimestamp(iso: string): string {
    const t = new Date(iso).getTime();
    if (!Number.isFinite(t)) return '';
    const diffMin = Math.round((Date.now() - t) / 60_000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.round(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.round(diffHr / 24);
    if (diffDay < 14) return `${diffDay}d ago`;
    return new Date(iso).toLocaleDateString();
}

function prettyError(e: unknown): string {
    if (!(e instanceof ApiError)) return 'Something went wrong.';
    switch (e.code) {
        case 'not_found':
            return 'This note no longer exists.';
        case 'body_edit_forbidden':
            return 'Only the author (or an admin) can edit the text.';
        case 'delete_forbidden':
            return 'Only the author (or an admin) can delete this note.';
        case 'body_too_long':
            return 'Note is too long. Keep it under 2000 characters.';
        case 'missing_body':
            return 'The note is empty.';
        case 'insufficient_role':
            return 'Your role can’t manage shared instructions.';
        default:
            return 'Something went wrong.';
    }
}
